import { useCallback, useEffect, useState } from "react";
import { usePwaContext } from "../context/pwa-context";
import useDisclosure from "./useDisclosure";
import {
  getStoredNotificationPromptTime,
  getStoredPwaInstallStatus,
  setStoredHasShownNotificationPrompt,
  setStoredNotificationPromptTime,
  setStoredPwaInstallStatus,
} from "../utils/storage";
import { showSuccessToast } from "../utils/show-toast";
import { AppType, openApp } from "../utils/helpers";
import Logger from "../utils/logger";
import { OperatingSystems } from "../utils/vendor";
import useDeviceInfo from "./useDeviceInfo";
import EventEmitter from "events";

export enum EnableNotificationsSteps {
  TURN_ON_NOTIFICATIONS,
  OPEN_CHROME_BROWSER,
}

export enum NotificationPermissionStatus {
  GRANTED = "granted",
  DENIED = "denied",
  DEFAULT = "default",
}

const notificationsEmitter = new EventEmitter();
const NOTIFICATIONS_ENABLED_EVENT = "notificationsEnabled";
const NOTIFICATION_PERMISSION_BROADCAST = "notification-permission";

const notificationPermissionBroadcaster = new BroadcastChannel(
  NOTIFICATION_PERMISSION_BROADCAST
);
const notificationPermissionReceiver = new BroadcastChannel(
  NOTIFICATION_PERMISSION_BROADCAST
);

export default function useNotifications() {
  const { userIsInPwa, doesRelatedAppExist } = usePwaContext();
  const { os, browserIsChrome } = useDeviceInfo();
  const [enableNotificationsStep, setEnableNotificationsStep] =
    useState<EnableNotificationsSteps>(
      EnableNotificationsSteps.TURN_ON_NOTIFICATIONS
    );
  const [notificationsAreEnabled, setNotificationsAreEnabled] =
    useState<boolean>(false);
  const {
    isOpen: isPwaInstallationDialogOpen,
    onOpen: openPwaInstallationDialog,
    onClose: closePwaInstallationDialog,
  } = useDisclosure();
  const {
    isOpen: isEnableNotificationsDialogOpen,
    onOpen: openEnableNotificationsDialog,
    onClose: closeEnableNotificationsDialog,
  } = useDisclosure();

  const resetAutomaticNotificationsParams = () => {
    setStoredHasShownNotificationPrompt(false);
    setStoredNotificationPromptTime(null);
  };

  // interval should be specified in hours. So, for a 3-day interval, 72 should be passed.
  const showPeriodicNotificationPrompt = useCallback(
    (interval: number) => {
      const previousInstallPromptTime = getStoredNotificationPromptTime();
      if (!previousInstallPromptTime) {
        setStoredNotificationPromptTime(Date.now());
        openEnableNotificationsDialog();
        return;
      }
      const timeDifference =
        Math.floor(Date.now() - previousInstallPromptTime) / (60 * 60 * 1000);
      if (timeDifference >= interval) {
        openEnableNotificationsDialog();
        setStoredNotificationPromptTime(Date.now());
      }
    },
    [openEnableNotificationsDialog]
  );

  const enableNotifications = () => {
    if (!("Notification" in window)) {
      // Check if the browser supports notifications
      Logger.log("This browser does not support notifications");
      return;
    }

    if (Notification.permission !== NotificationPermissionStatus.DEFAULT)
      return;
    Notification.requestPermission().then((permission) => {
      if (permission === NotificationPermissionStatus.GRANTED) {
        setNotificationsAreEnabled(true);
        showSuccessToast("Notifications enabled!");
        // Broadcast the updated notification permission status across browsing contexts
        notificationPermissionBroadcaster.postMessage({
          type: NOTIFICATIONS_ENABLED_EVENT,
          status: true,
        });
      }
    });
  };

  const handleNotifyMe = async () => {
    if (userIsInPwa) {
      if (!notificationsAreEnabled) {
        enableNotifications();
      }
      isEnableNotificationsDialogOpen && closeEnableNotificationsDialog();
      return;
    }

    const pwaExists = await doesRelatedAppExist(AppType.PWA);
    if (pwaExists) {
      openApp();
      // I called resetAutomaticNotificationsParams instead of openEnableNotificationsDialog because the latter results
      // in the immediate trigger of the EnableNotifications prompt, i.e., before the PWA has successfully opened.
      // resetAutomaticNotificationsParams on the other hand just resets the params that determine when the prompt should be automatically displayed,
      // thus I'm leveraging the side effect to cause a delay in the display of the prompt during which time the PWA must have successfully opened.
      if (!notificationsAreEnabled) resetAutomaticNotificationsParams();
      return;
    }

    if (os === OperatingSystems.Android && !browserIsChrome) {
      setEnableNotificationsStep(EnableNotificationsSteps.OPEN_CHROME_BROWSER);
      !isEnableNotificationsDialogOpen && openEnableNotificationsDialog();
    } else {
      openPwaInstallationDialog();
    }
  };

  // Reset the step for the EnableNotifications dialog when the user closes the dialog
  useEffect(() => {
    const resetEnableNotificationsStep = () => {
      if (!isEnableNotificationsDialogOpen) {
        setEnableNotificationsStep(
          EnableNotificationsSteps.TURN_ON_NOTIFICATIONS
        );
      }
    };

    // Delay the reset to prevent the user from seeing a flash of updated content while the dialog is sliding out of view
    const resetTimeoutId = setTimeout(resetEnableNotificationsStep, 1500);

    return () => {
      clearTimeout(resetTimeoutId);
    };
  }, [isEnableNotificationsDialogOpen]);

  // Check if notifications have been enabled
  useEffect(() => {
    if (!("Notification" in window)) {
      // eslint-disable-next-line no-console
      Logger.log("This browser does not support notifications");
      return;
    }
    if (Notification.permission === NotificationPermissionStatus.GRANTED) {
      setNotificationsAreEnabled(true);
    } else {
      setNotificationsAreEnabled(false);
    }
  }, []);

  // Subscribe to the notificationsEnabled event so that all consumers of the useNotifications hook within the
  // specific browsing context will get the updated state when notifications are enabled and show the appropriate UI
  useEffect(() => {
    const handleNotificationsEnabled = (updatedStatus: boolean) => {
      setNotificationsAreEnabled(updatedStatus);
    };
    notificationsEmitter.on(
      NOTIFICATIONS_ENABLED_EVENT,
      handleNotificationsEnabled
    );

    return () => {
      notificationsEmitter.off(
        NOTIFICATIONS_ENABLED_EVENT,
        handleNotificationsEnabled
      );
    };
  }, []);

  // Subscribe to notification permission status broadcasts and update the related state.
  // Essentially, the broadcast channel strategy handles communication between the PWA and the browser
  // While event emitting ensures that all consumers of the hook within each browsing context (i.e. PWA and browser)
  // receive the updated "notificationsAreEnabled" state.
  useEffect(() => {
    notificationPermissionReceiver.onmessage = (event) => {
      if (event.data.type === NOTIFICATIONS_ENABLED_EVENT) {
        notificationsEmitter.emit(
          NOTIFICATIONS_ENABLED_EVENT,
          event.data.status
        );
      }
    };

    return () => {
      notificationPermissionReceiver.close();
    };
  }, []);

  // Open prompt to enable notifications when the user is opening the PWA for the first time after installation
  useEffect(() => {
    if (!userIsInPwa) return;
    const pwaInstallStatus = getStoredPwaInstallStatus();

    if (os === OperatingSystems.Ios) {
      if (!pwaInstallStatus) {
        setStoredPwaInstallStatus({
          openedPwa: false,
          installationDate: Date.now(),
        });
      }
    }

    if (pwaInstallStatus && !pwaInstallStatus.openedPwa) {
      !isEnableNotificationsDialogOpen && openEnableNotificationsDialog();
      setStoredPwaInstallStatus({
        ...pwaInstallStatus,
        openedPwa: true,
      });
    }
  }, [
    os,
    userIsInPwa,
    isEnableNotificationsDialogOpen,
    openEnableNotificationsDialog,
  ]);

  return {
    userIsInPwa,
    notificationsAreEnabled,
    enableNotificationsStep,
    handleNotifyMe,
    isPwaInstallationDialogOpen,
    isEnableNotificationsDialogOpen,
    showPeriodicNotificationPrompt,
    openEnableNotificationsDialog,
    closePwaInstallationDialog,
    closeEnableNotificationsDialog,
  };
}
